import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormsModule,
  Validators,
  FormBuilder,
  FormControl,
  FormArray,
  AbstractControl,
} from "@angular/forms";
import { ActivatedRoute } from '@angular/router';
import { InventoryService } from 'src/app/service/inventory.service';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { alertModal, alertModalDelete, alertModalWithoutConfirm } from '../../helpers/alert';
import { readBlobError, saveBlob } from '../../helpers/helper';
import { monitorEventLoopDelay } from 'perf_hooks';
import { clippingParents } from '@popperjs/core';
import { NoDataComponent } from "../shared/no-data/no-data.component";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { environment } from 'src/environments/environment';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { WORKSTATION_FILTER, SORT_BY, SORT_ORDER } from 'src/app/constants/workstation.constant';
import { AssetHistoryComponent } from '../shared/asset-history/asset-history.component';
import { canViewAssetLogs } from 'src/app/service/asset-logs.service';
import { isHrOrItAdminDept } from 'src/app/constants/departments';


interface userList {
  id: string;
  viewValue: string;
}


@Component({
  selector: 'app-workstation',
  imports: [InfiniteScrollModule, MatInputModule, MatAutocompleteModule, MatSelectModule, MatFormFieldModule, ReactiveFormsModule, FormsModule, NgClass, NgStyle, NoDataComponent, AssetHistoryComponent],
  templateUrl: './workstation.component.html',
  styleUrls: ['./workstation.component.css', '../styles/inventory.css']
})
export class WorkstationComponent implements OnInit {
  canViewAssetLogs = false;
  activeDetailTab: 'details' | 'history' = 'details';

  currentStep = 1;
  selectedImage: File | string | null = null;
  selectedImages: (File | string)[] = [];
  previewImageUrl: string | ArrayBuffer | null = null;
  previewImagesUrl: string[] = [];
  maxImages: number = 5;
  showWorkstationButton = false;
  userDepartment: string | null = null;
  canAccessProductKey: boolean = false;
  canExportBarcodes = false;
  selectedWorkstationIds = new Set<string>();
  isExportingBarcodes = false;
  isProductKeyMasked: boolean = true;
  productKeyCopied: boolean = false;

  selectedFilter: WORKSTATION_FILTER | string = WORKSTATION_FILTER.ALL;
  WORKSTATION_FILTER = WORKSTATION_FILTER;

  selectedSort: string = 'newest';
  sortBy: SORT_BY = SORT_BY.CREATED_AT;
  sortOrder: SORT_ORDER = SORT_ORDER.DESC;
  SORT_BY = SORT_BY;
  SORT_ORDER = SORT_ORDER;

  users: { value: string, viewValue: string, id: string }[] = [];

  configurationForm: FormGroup;
  accessoriesForm: FormGroup;

  //shimmer loader
  isShimmerLoading: boolean = true;
  shimmerLoadingValue = ([] = [1, 2, 3, 4,]);
  isFormSubmitting: boolean = false;

  constructor(private fb: FormBuilder,
    private inventoryService: InventoryService,
    private route: ActivatedRoute
  ) {

    this.configurationForm = this.fb.group({
      workstationName: ['', [Validators.required, Validators.maxLength(50), this.noWhitespaceValidator]],
      workstationId: ['', [Validators.required, Validators.maxLength(25), this.noWhitespaceValidator]],
      processor: ['', [Validators.required, Validators.maxLength(50), this.noWhitespaceValidator]],
      graphicCard: ['', [Validators.required, Validators.maxLength(50), this.noWhitespaceValidator]],
      isLicensed: ['', Validators.required],
      productKey: [''],
      location: [
        "",
        [
          Validators.required,
          Validators.maxLength(50),
          this.noWhitespaceValidator,
        ],
      ],
      assignTo: ['', Validators.required],
      ramValue: ['', [Validators.required, Validators.maxLength(50), this.noWhitespaceValidator]],
      version: ['', [Validators.required, Validators.maxLength(50), this.noWhitespaceValidator]],
      WorkstationFile: []
    })

    this.accessoriesForm = this.fb.group({
      monitor1: ['', [Validators.required, Validators.maxLength(50), this.noWhitespaceValidator]],
      monitor2: ['', [Validators.maxLength(50)]],
      monitor3: ['', [Validators.maxLength(50)]],
      monitor4: ['', [Validators.maxLength(50)]],
      monitor5: ['', [Validators.maxLength(50)]],
      monitor6: ['', [Validators.maxLength(50)]],
      headPhone: ['', [Validators.maxLength(50)]],
      keyboard: ['', [Validators.maxLength(50)]],
      mouse: ['', [Validators.maxLength(50)]],
      camera: ['', [Validators.maxLength(50)]],
      speakers: ['', [Validators.maxLength(50)]]
    });

  }


  ngOnInit(): void {
    this.getAllUsers();
    this.userDepartment = (localStorage.getItem('department') || '').trim().toUpperCase();
    this.canViewAssetLogs = canViewAssetLogs();
    const manageRoles = ['OPERATIONS', 'OFFICE ADMIN', 'ADMIN'];
    this.showWorkstationButton = isHrOrItAdminDept(this.userDepartment) || manageRoles.includes(this.userDepartment);
    this.canAccessProductKey = ['HR-HEAD', 'ADMIN'].includes(this.userDepartment);
    this.canExportBarcodes = isHrOrItAdminDept(this.userDepartment) || manageRoles.includes(this.userDepartment);

    this.route.queryParams.subscribe((params) => {
      const filterParam = params['filter'];
      if (filterParam === WORKSTATION_FILTER.ASSIGNED || filterParam === 'assigned') {
        this.selectedFilter = WORKSTATION_FILTER.ASSIGNED;
      } else if (
        filterParam === WORKSTATION_FILTER.ASSIGNED_MINE ||
        filterParam === 'assigned_mine' ||
        filterParam === 'onlyAssigned'
      ) {
        this.selectedFilter = WORKSTATION_FILTER.ASSIGNED_MINE;
      } else if (filterParam === WORKSTATION_FILTER.ALL || filterParam === 'all') {
        this.selectedFilter = WORKSTATION_FILTER.ALL;
      }
      this.getWorkstation(true);
    });
  }


  filteredUsers: any[] = [];
  searchTerm: string = '';
  allUsers: any[] = [];


  // Add ViewChild references
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('userSelect') userSelect!: MatSelect;
  getAllUsers(): void {
    this.inventoryService.getInvUsers().subscribe(res => {
      const data = res?.data || [];

      this.allUsers = data.map((user: any) => ({
        value: user.userName,
        viewValue: user.userName,
        id: user._id
      }));

      this.users = [...this.allUsers];
      this.filteredUsers = [...this.allUsers];

      if (this.isEdit && (this.singleWorkstation || this.workstationDetail)) {
        this.patchAssigneeUser();
      }
    });
  }



  onUserSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();

    this.filteredUsers = this.allUsers.filter(user =>
      user.viewValue.toLowerCase().includes(value)
    );
  }

  selectedUser: any = null;

  onUserSelected(user: any): void {
    this.selectedUser = user;
    this.configurationForm.get('assignTo')?.setValue(user);
  }



  onSelectOpened(opened: boolean): void {
    if (opened && this.searchInput) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 0);
    }
  }

  displayUser = (user: any): string => {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return user?.viewValue || user?.value || user?.userName || '';
  };


  navigateToStep(step: number): void {
    this.currentStep = step;
  }

  resetClose() {
    this.configurationForm.reset();
    this.accessoriesForm.reset();
    this.currentStep = 1;
    this.selectedImages = [];
    this.previewImagesUrl = [];
    this.selectedUser = null;
    this.singleWorkstation = null;
    this.isEdit = false;
  }

  previewInventory: any
  showPreviewModal = false;
  activeSlideIndex = 0;
  openPreviewSlider(index: number) {
    this.previewInventory = { ...this.workstation[index] };
    this.activeSlideIndex = 0;
    this.showPreviewModal = true;
  }

  closePreviewSlider() {
    this.showPreviewModal = false;
  }

  prevSlide() {
    if (!this.previewInventory?.images?.length) return;
    this.activeSlideIndex =
      (this.activeSlideIndex - 1 + this.previewInventory.images.length) %
      this.previewInventory.images.length;
  }

  nextSlide() {
    if (!this.previewInventory?.images?.length) return;
    this.activeSlideIndex =
      (this.activeSlideIndex + 1) % this.previewInventory.images.length;
  }

  goToSlide(index: number) {
    this.activeSlideIndex = index;
  }

  isEditMode: boolean;
  previewProductImg: any
  workstationDetail: any
  openWorkstationDetail(index: number) {
    this.isEditMode = true;
    this.activeDetailTab = 'details';
    this.isProductKeyMasked = true;
    this.productKeyCopied = false;
    this.previewProductImg = this.workstation[index];
    this.workstationDetail = this.workstation[index];
    this.editWorkstationId = this.workstation[index]?._id || this.workstation[index]?.workstationId;
    console.log({ Detail: this.workstationDetail });
  }

  toggleProductKeyMask(): void {
    this.isProductKeyMasked = !this.isProductKeyMasked;
  }

  copyProductKey(key?: string): void {
    if (!key) return;
    navigator.clipboard.writeText(key).then(() => {
      this.productKeyCopied = true;
      setTimeout(() => {
        this.productKeyCopied = false;
      }, 2000);
    });
  }

  // Configuration Image Upload
  newImages: File[] = [];
  onSelectImage(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const selectedImage = event.target.files[0];

      this.newImages = [selectedImage];
      this.selectedImages = [...this.selectedImages, ...this.newImages];

      this.updateImagePreviews();

      this.configurationForm.patchValue({
        WorkstationFile: this.selectedImages,
      });
      console.log("Selected images (including existing):", this.selectedImages);
    }
  }

  // Configuration Multiple Image Upload
  onSelectMultipleImages(event: any): void {

    if (event.target.files && event.target.files.length > 0) {
      const filesArray: File[] = Array.from(event.target.files) as File[];
      const totalImages = this.selectedImages.length + filesArray.length;

      if (totalImages > this.maxImages) {
        return alertModalWithoutConfirm(
          "warning",
          `You can only upload a maximum of ${this.maxImages} images.`
        );
      }
      this.newImages = filesArray;
      this.selectedImages = [...this.selectedImages, ...this.newImages];

      this.selectedImages = Array.from(new Set(this.selectedImages));

      this.updateImagePreviews();
      this.configurationForm.patchValue({
        WorkstationFile: this.selectedImages
      });

    }
    console.log("Selected images (including existing):", this.selectedImages);
  }

  updateImagePreviews(): void {
    this.previewImagesUrl = [];
    this.selectedImages.forEach((image) => {
      if (typeof image === "string") {
        this.previewImagesUrl.push(image);
      } else {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.previewImagesUrl.push(e.target.result);
        };
        reader.readAsDataURL(image);
      }
    });
  }

  // // Remove multiple uploaded Image
  // removeImage(index: number): void {
  //   this.selectedImages.splice(index, 1);
  //   this.updateImagePreviews();
  //   this.configurationForm.patchValue({
  //     WorkstationFile: this.selectedImages
  //   });
  // }


  removeImage(index: number): void {
    const removedImage = this.selectedImages[index];

    if (this.isEdit && typeof removedImage === 'string') {
      const body = {
        inventoryId: this.editWorkstationId,
        images: [removedImage],
      };

      this.inventoryService.deleteWorkstationImage(this.editWorkstationId, body).subscribe(
        (response) => {
          console.log('Image removed successfully from server:', response);
          this.removeImageLocally(index);
        },
        (error) => {
          console.error('Failed to remove image from server', error);
          // Still remove locally to update UI if needed, or don't? 
          // Usually better to only remove on success or warn user.
          this.removeImageLocally(index);
        }
      );
    } else {
      this.removeImageLocally(index);
    }
  }

  removeImageLocally(index: number): void {
    this.selectedImages.splice(index, 1);
    this.updateImagePreviews();
    this.configurationForm.patchValue({
      WorkstationFile: this.selectedImages,
    });
  }


  submitted = false;
  //Configuration Form Submit
  onSubmitConfiguration() {
    console.log(this.configurationForm)
    this.submitted = true;
    if (this.configurationForm.valid) {
      this.currentStep = 2;
      const formData = new FormData();
      Object.keys(this.configurationForm.value).forEach(key => {
        formData.append(key, this.configurationForm.value[key]);
      });

      if (this.selectedImages.length > 0) {
        this.selectedImages.forEach((image, index) => {
          formData.append(`files[${index}]`, image);
        });
      }
    }
    else if (this.configurationForm.invalid) {
      console.log('invalid')
      return alertModalWithoutConfirm(
        "warning",
        "Please fill in all required fields before submitting."
      );
    }

  }

  selectedAssigneeId: string | null = null;
  onSubmitAccessories() {
    console.log("isEdit value:", this.isEdit);
    console.log(this.accessoriesForm);

    if (this.accessoriesForm.invalid) {
      return alertModalWithoutConfirm(
        "warning",
        "Please fill in all required fields before submitting."
      );
    }
    if (this.configurationForm.valid) {
      const workstationFormValue = {
        ...this.configurationForm.value,
        ...this.accessoriesForm.value
      };
      const selectedAssignee = workstationFormValue.assignTo;
      let assigneeId = typeof selectedAssignee === 'object' ? selectedAssignee?.id || null : null;
      let assigneeName = typeof selectedAssignee === 'object' ? (selectedAssignee?.viewValue || selectedAssignee?.value || '') : (selectedAssignee || '');

      if (!assigneeId && assigneeName && this.allUsers.length > 0) {
        const found = this.allUsers.find(
          u => u.value?.toLowerCase() === String(assigneeName).toLowerCase() || u.viewValue?.toLowerCase() === String(assigneeName).toLowerCase()
        );
        if (found) {
          assigneeId = found.id;
          assigneeName = found.viewValue;
        }
      }
      console.log('Assignee:', selectedAssignee);
      console.log({ assigneeId, assigneeName });

      const monitorArray = [
        workstationFormValue.monitor1,
        workstationFormValue.monitor2,
        workstationFormValue.monitor3,
        workstationFormValue.monitor4,
        workstationFormValue.monitor5,
        workstationFormValue.monitor6
      ].filter(monitor => monitor);

      const getFormValue: any = {
        name: String(workstationFormValue.workstationName),
        assigneeName: assigneeName,
        assigneeId: assigneeId,
        processor: workstationFormValue.processor,
        ram: workstationFormValue.ramValue,
        graphicsCard: workstationFormValue.graphicCard,
        location: workstationFormValue.location,
        windowsVersion: workstationFormValue.version,
        windowsLicensed: Boolean(workstationFormValue.isLicensed === 'yes'),
        monitors: monitorArray,
        camera: workstationFormValue.camera || "",
        speakers: workstationFormValue.speakers || "",
        workstationId: String(workstationFormValue.workstationId),
        redirectUrl: `${environment.redirectUrl}#/inventory/workstation`,

      };

      if (workstationFormValue.headPhone) {
        getFormValue.headPhone = workstationFormValue.headPhone;
      }
      if (workstationFormValue.keyboard) {
        getFormValue.keyboard = workstationFormValue.keyboard;
      }
      if (workstationFormValue.mouse) {
        getFormValue.mouse = workstationFormValue.mouse;
      }

      const selectedImages = this.configurationForm.get("WorkstationFile")?.value;

      if (!this.isEdit) {
        const formData = new FormData();
        for (const key in getFormValue) {
          if (getFormValue.hasOwnProperty(key)) {
            if (Array.isArray(getFormValue[key])) {
              getFormValue[key].forEach((item, index) => {
                formData.append(`monitors[${index}]`, item);
              });
            } else {
              formData.append(key, getFormValue[key]);
            }
          }
        }

        // Only append productKey when user has access and value is not empty
        if (this.canAccessProductKey) {
          const rawKey = workstationFormValue.productKey;
          const trimmedKey = typeof rawKey === 'string' ? rawKey.trim() : (rawKey ? String(rawKey).trim() : '');
          if (trimmedKey !== '') {
            formData.append('productKey', trimmedKey);
          }
        }

        if (selectedImages && Array.isArray(selectedImages)) {
          let newIndex = 0;
          selectedImages.forEach(async (image: File | string) => {
            if (typeof image === "string") {
              const blob = await fetch(image).then(res => res.blob());
              formData.append(`files[${newIndex}]`, blob, "blob");
            } else {
              formData.append(`files[${newIndex}]`, image, image.name);
            }
            newIndex++;
          });
        }
        this.isFormSubmitting = true;
        this.inventoryService.addWorkstation(formData).subscribe(
          response => {
            this.currentStep = 3;
            console.log(response, "response");
            this.getWorkstation();
            this.isFormSubmitting = false;
          },
          error => {
            console.error('error:', error);
            this.isFormSubmitting = false;
          }
        );
      } else {
        console.log("Updating workstation with ID:", this.editWorkstationId);

        const requestBody: any = { ...getFormValue };
        if (this.canAccessProductKey) {
          const rawKey = workstationFormValue.productKey;
          requestBody.productKey = (rawKey !== undefined && rawKey !== null) ? String(rawKey).trim() : '';
        } else {
          delete requestBody.productKey;
        }

        this.inventoryService.updateWorkstation(this.editWorkstationId, requestBody).subscribe(
          response => {
            this.currentStep = 3;
            console.log("Updated successfully:", response);

            if (selectedImages && Array.isArray(selectedImages)) {
              console.log("Uploading files after workstation update");
              this.uploadFiles(selectedImages, this.editWorkstationId);
            }
            this.isFormSubmitting = false;
            this.getWorkstation();
          },
          error => {
            console.error("Update failed:", error);
          }
        );
      }
    } else {
      console.log("One or more forms are invalid");
      alert("Please fill in all required fields before submitting.");
    }
  }

  // Helper method to handle file uploads separately
  uploadFiles(files: (File | string)[], workstationId: string) {
    const formData = new FormData();
    const selectedImages = this.configurationForm.get("WorkstationFile")?.value || this.selectedImages;

    if (selectedImages && Array.isArray(selectedImages)) {
      const newFiles = selectedImages.filter((img: any) => img instanceof File);
      if (newFiles.length === 0) return;

      let newIndex = 0;
      newFiles.forEach((image: File) => {
        formData.append(`files[${newIndex}]`, image, image.name);
        newIndex++;
      });

      this.inventoryService.uploadFiles(formData, workstationId).subscribe(
        response => {
          console.log("Files uploaded successfully:", response);
        },
        error => {
          console.error("File upload failed:", error);
        }
      );
    }
  }




  workstation: any[] = [];
  totalWorkstation: number = 0;
  currentPageNume: number = 1;
  currentLimit: number = 100;

  onFilterChange(filter: WORKSTATION_FILTER): void {
    this.selectedFilter = filter;
    this.searchQuery = '';
    this.getWorkstation(true);
  }

  onSortDropdownChange(sortType: string): void {
    this.selectedSort = sortType;
    if (sortType === 'newest') {
      this.sortBy = SORT_BY.CREATED_AT;
      this.sortOrder = SORT_ORDER.DESC;
    } else if (sortType === 'oldest') {
      this.sortBy = SORT_BY.CREATED_AT;
      this.sortOrder = SORT_ORDER.ASC;
    } else if (sortType === 'ascName') {
      this.sortBy = SORT_BY.NAME;
      this.sortOrder = SORT_ORDER.ASC;
    } else if (sortType === 'descName') {
      this.sortBy = SORT_BY.NAME;
      this.sortOrder = SORT_ORDER.DESC;
    } else if (sortType === 'dateModified') {
      this.sortBy = SORT_BY.UPDATED_AT;
      this.sortOrder = SORT_ORDER.DESC;
    }
    this.getWorkstation(true);
  }

  onSort(sortField: SORT_BY): void {
    if (this.sortBy === sortField) {
      this.sortOrder = this.sortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC;
    } else {
      this.sortBy = sortField;
      this.sortOrder = SORT_ORDER.DESC;
    }
    if (this.sortBy === SORT_BY.CREATED_AT && this.sortOrder === SORT_ORDER.DESC) {
      this.selectedSort = 'newest';
    } else if (this.sortBy === SORT_BY.CREATED_AT && this.sortOrder === SORT_ORDER.ASC) {
      this.selectedSort = 'oldest';
    } else if (this.sortBy === SORT_BY.NAME && this.sortOrder === SORT_ORDER.ASC) {
      this.selectedSort = 'ascName';
    } else if (this.sortBy === SORT_BY.NAME && this.sortOrder === SORT_ORDER.DESC) {
      this.selectedSort = 'descName';
    } else if (this.sortBy === SORT_BY.UPDATED_AT && this.sortOrder === SORT_ORDER.DESC) {
      this.selectedSort = 'dateModified';
    } else {
      this.selectedSort = '';
    }
    this.getWorkstation(true);
  }

  get selectedVisibleWorkstationIds(): string[] {
    return this.workstation
      .map((item: any) => item?._id)
      .filter((id: string) => id && this.selectedWorkstationIds.has(id));
  }

  get allWorkstationsSelected(): boolean {
    return this.workstation.length > 0 && this.selectedVisibleWorkstationIds.length === this.workstation.length;
  }

  toggleWorkstationSelection(id: string, checked: boolean) {
    if (checked) {
      this.selectedWorkstationIds.add(id);
    } else {
      this.selectedWorkstationIds.delete(id);
    }
  }

  toggleAllWorkstationSelection(checked: boolean) {
    this.selectedWorkstationIds.clear();
    if (checked) {
      this.workstation.forEach((item: any) => item?._id && this.selectedWorkstationIds.add(item._id));
    }
  }

  exportBarcodes() {
    if (this.isExportingBarcodes) return;
    this.isExportingBarcodes = true;
    this.inventoryService.exportWorkstationBarcodes(this.selectedVisibleWorkstationIds).subscribe({
      next: (blob) => {
        saveBlob(blob, 'workstation-barcodes.xlsx');
        this.selectedWorkstationIds.clear();
        this.isExportingBarcodes = false;
      },
      error: async (error) => {
        this.isExportingBarcodes = false;
        alertModalWithoutConfirm('error', await readBlobError(error));
      },
    });
  }

  getWorkstation(reset: boolean = false) {
    if (reset) {
      this.currentPageNume = 1;
      this.workstation = [];
      this.isShimmerLoading = true;
    }

    const isStandardFilter =
      this.selectedFilter === WORKSTATION_FILTER.ALL ||
      this.selectedFilter === WORKSTATION_FILTER.ASSIGNED ||
      this.selectedFilter === WORKSTATION_FILTER.ASSIGNED_MINE;

    const field: any = {
      pageNum: this.currentPageNume,
      limit: this.currentLimit,
      filter: isStandardFilter ? this.selectedFilter : WORKSTATION_FILTER.ASSIGNED,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    };

    if (!isStandardFilter && this.selectedFilter) {
      field.assignedTo = this.selectedFilter;
    }

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      field.searchQuery = this.searchQuery.trim();
    }

    this.inventoryService.getWorkstation(field).subscribe({
      next: (response: any) => {
        const newItems = response?.data?.records || [];

        if (this.currentPageNume === 1) {
          this.workstation = [...newItems];
        } else {
          this.workstation = [...this.workstation, ...newItems];
        }
        this.totalWorkstation = response?.data?.totalCounts || 0;
        this.isShimmerLoading = false;
        console.log('Response', response);
        console.log('Fetching Page:', this.currentPageNume, 'Limit:', this.currentLimit);
      },
      error: (error) => {
        console.error('Error fetching workstations:', error);
        this.isShimmerLoading = false;
      }
    });
  }

  onScroll = () => {
    const totalLoaded = this.workstation.length;
    if (totalLoaded >= this.totalWorkstation) return;
    this.currentPageNume++;
    this.getWorkstation(false);
  };

  searchQuery: string = '';
  onSearchInventory() {
    this.getWorkstation(true);
  }

  clearSearch() {
    this.searchQuery = '';
    this.getWorkstation(true);
  }

  editWorkstationId: string;
  singleWorkstation: any;
  getSingleworkstation(id: any) {
    this.editWorkstationId = id;
    this.inventoryService.getWorkstationById(id).subscribe((res) => {
      this.singleWorkstation = res["data"];
      console.log("Workstation BY ID", this.singleWorkstation);
      if (this.singleWorkstation) {
        this.workstationDetail = {
          ...this.workstationDetail,
          ...this.singleWorkstation
        };
        this.editWorkstation();
      }
    });

  }

  isDeleteModal: boolean = false;
  openDeleteModal() {
    console.log("clicked")
    this.isDeleteModal = true
  }

  // Delete Workstation
  deleteWorkstation(id: any) {
    this.editWorkstationId = id;
    console.log(this.editWorkstationId, "ID");
    this.inventoryService.deleteWorkstation(id).subscribe(
      (res) => {
        this.isDeleteModal = false;
        alertModal("success", "Deleted successfully");

        setTimeout(() => {
          window.location.reload();
        }, 1500);
      },
      (error) => {
        console.error(error);
        const message = error.error.message || "Something went wrong!";
        alertModalDelete(message);
      },
    );
  }


  noWhitespaceValidator(control: AbstractControl): { [key: string]: any } | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    return !isWhitespace ? null : { whitespace: true };
  }


  patchAssigneeUser(): void {
    const ws = this.singleWorkstation || this.workstationDetail || {};
    const targetId = ws.assigneeId || ws.assignedToId || ws.assignToId;
    const targetName = ws.assigneeName || ws.assignedTo || ws.assignTo;

    let assignee: any = null;
    if (this.allUsers && this.allUsers.length > 0) {
      assignee = this.allUsers.find(
        user => (targetId && (user.id === targetId || user.value === targetId)) ||
          (targetName && (user.value?.toLowerCase() === String(targetName).trim().toLowerCase() ||
            user.viewValue?.toLowerCase() === String(targetName).trim().toLowerCase()))
      );
    }

    if (!assignee && targetName && targetName !== ' - ' && targetName !== '-') {
      assignee = { value: targetName, viewValue: targetName, id: targetId || '' };
    }

    if (assignee) {
      this.selectedUser = assignee;
      this.configurationForm.get('assignTo')?.setValue(assignee);
    }
  }

  isEdit: boolean = false;
  editWorkstation() {
    this.currentStep = 1;
    this.isEdit = true;

    const ws = this.singleWorkstation || this.workstationDetail || {};
    console.log('singleWorkstation:', ws);

    // Populate existing images
    let existingImages: string[] = [];
    if (Array.isArray(ws.images) && ws.images.length > 0) {
      existingImages = ws.images.filter((img: any) => typeof img === 'string' && img.trim() !== '');
    } else if (ws.image && typeof ws.image === 'string') {
      existingImages = [ws.image];
    }

    this.selectedImages = [...existingImages];
    this.previewImagesUrl = [...existingImages];

    this.configurationForm.patchValue({
      workstationName: ws.name || '',
      processor: ws.processor || '',
      graphicCard: ws.graphicsCard || ws.graphicCard || '',
      location: ws.location || '',
      isLicensed: ws.windowsLicensed || ws.licensed ? 'yes' : 'no',
      productKey: this.canAccessProductKey ? (ws.productKey || '') : '',
      ramValue: ws.ram || ws.ramValue || '',
      version: ws.windowsVersion || ws.version || '',
      WorkstationFile: this.selectedImages,
      workstationId: ws.workstationId || ''
    });

    this.patchAssigneeUser();

    // Also patch accessories if present
    const monitors = Array.isArray(ws.monitors) ? ws.monitors : (Array.isArray(ws.monitorDetails) ? ws.monitorDetails : []);
    this.accessoriesForm.patchValue({
      monitor1: monitors[0] && monitors[0] !== 'null' && monitors[0] !== '-' ? monitors[0] : '',
      monitor2: monitors[1] && monitors[1] !== 'null' && monitors[1] !== '-' ? monitors[1] : '',
      monitor3: monitors[2] && monitors[2] !== 'null' && monitors[2] !== '-' ? monitors[2] : '',
      monitor4: monitors[3] && monitors[3] !== 'null' && monitors[3] !== '-' ? monitors[3] : '',
      monitor5: monitors[4] && monitors[4] !== 'null' && monitors[4] !== '-' ? monitors[4] : '',
      monitor6: monitors[5] && monitors[5] !== 'null' && monitors[5] !== '-' ? monitors[5] : '',
      headPhone: ws.headPhone && ws.headPhone !== ' - ' ? ws.headPhone : '',
      keyboard: ws.keyboard && ws.keyboard !== ' - ' ? ws.keyboard : '',
      mouse: ws.mouse && ws.mouse !== ' - ' ? ws.mouse : '',
      camera: ws.camera && ws.camera !== ' - ' && ws.camera !== 'null' ? ws.camera : '',
      speakers: ws.speakers && ws.speakers !== ' - ' && ws.speakers !== 'null' ? ws.speakers : '',
    });
  }


  removeAssignee(workstationId: any) {
    const body = {
      workstationId: this.editWorkstationId,
    };
    console.log(body)
    this.inventoryService.removeWorkstationAssignee(body).subscribe(
      (data) => {
        console.log("Assignee Removed");
        this.configurationForm.get('assignTo')?.setValue(null);
      },
      (error) => {
        console.error("Error removing assignee:", error);

      }
    );
  }

}




